import * as React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { logger } from '../../logging';
import { getMarkdownSafetyError } from './markdownSafety';

const markdownStyles = require('./markdown.css');

const renderErrorMessage = 'Unable to render this Markdown safely.';

export interface MarkdownRendererProps {
  markdown: string;
  className?: string;
  maxLength?: number;
}

interface MarkdownLinkProps {
  children?: React.ReactNode;
  href?: string;
  title?: string;
}

interface MarkdownImageProps {
  alt?: string;
}

interface MarkdownErrorBoundaryProps {
  markdown: string;
  children: React.ReactNode;
}

interface MarkdownErrorBoundaryState {
  hasError: boolean;
}

class MarkdownErrorBoundary extends React.Component<
  MarkdownErrorBoundaryProps,
  MarkdownErrorBoundaryState
> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    logger.error(`[MarkdownRenderer] Unable to render Markdown: ${error}`);
  }

  componentDidUpdate(previousProps: MarkdownErrorBoundaryProps) {
    if (this.state.hasError && previousProps.markdown !== this.props.markdown) {
      this.setState({ hasError: false });
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <p className={markdownStyles.unsafeMarkdown} role="alert">
          {renderErrorMessage}
        </p>
      );
    }

    return this.props.children;
  }
}

const blockedUri = 'javascript:void(0)';

const SafeLink = ({ children, href, title }: MarkdownLinkProps) => {
  if (!href || href === blockedUri) {
    return <React.Fragment>{children}</React.Fragment>;
  }

  return (
    <a href={href} title={title} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
};

const ImageAltText = ({ alt }: MarkdownImageProps) => (
  <React.Fragment>{alt || ''}</React.Fragment>
);

export const MarkdownRenderer = ({
  markdown,
  className,
  maxLength,
}: MarkdownRendererProps) => {
  const safetyError = getMarkdownSafetyError(markdown, maxLength);
  if (safetyError) {
    return (
      <p className={markdownStyles.unsafeMarkdown} role="alert">
        {safetyError}
      </p>
    );
  }

  const markdownClassName = [markdownStyles.markdownContent, className]
    .filter(Boolean)
    .join(' ');

  return (
    <MarkdownErrorBoundary markdown={markdown}>
      <ReactMarkdown
        className={markdownClassName}
        remarkPlugins={[remarkGfm]}
        skipHtml={true}
        transformLinkUri={ReactMarkdown.uriTransformer}
        components={{
          a: SafeLink,
          img: ImageAltText,
        }}
      >
        {markdown}
      </ReactMarkdown>
    </MarkdownErrorBoundary>
  );
};
